import {
  Component,
  HostBinding,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { TranslateService } from '@ngx-translate/core';

import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { CoreMenuService } from 'src/@core/components/core-menu/core-menu.service';
import { CoreSidebarService } from 'src/@core/components/core-sidebar/core-sidebar.service';
import { CoreConfigService } from 'src/@core/services/config.service';
import { CoreMediaService } from 'src/@core/services/media.service';
import { status } from 'src/app/helpers/Interfaces/status-interface';
import { Nullable, int } from 'src/app/helpers/Types/types';
import { DataService } from 'src/app/helpers/data.service';
import { AttendanceService } from 'src/app/modules/hr360/attendance/attendance.service';
import { AuthService } from 'src/app/modules/app/auth/auth.service';
import { EmployeeService } from 'src/app/modules/hr360/employee/employee.service';
import { iStationResult } from 'src/app/helpers/Interfaces/station';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AddComponent } from 'src/app/modules/hr360/attendance/add/add.component';
import { environment } from 'src/environments/environment.development';
import {
  ReleaseNotesGateService,
  ReleaseNotesUiService,
} from 'src/app/modules/i-team/release-notes/release-notes-gate.service';
import { CustomerPortalService } from 'src/app/modules/invoicing/customer-portal/customer-portal.service';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class NavbarComponent implements OnInit, OnDestroy {
  @ViewChild('attendanceAdd') attendanceAdd!: AddComponent;
  public horizontalMenu!: boolean;
  public hiddenMenu!: boolean;
  public showSetupLanguageSwitcher = false;
  public isCustomerPortalUser = false;
  public currentLanguage: 'en' | 'ar' = 'en';
  public languageDropdownOpen = false;

  hasActiveReleaseNotes$ = this._releaseNotesGate.hasActive$;

  authUrl = environment.authUrl;

  public coreConfig: any;
  public currentSkin!: string;
  public prevSkin!: string;

  public languageOptions: any;
  public navigation: any;
  public selectedLanguage: any;
  public currentUser: any;
  protected role: string;
  protected statusColor!: Nullable<string>;
  protected attendanceColor!: Nullable<string>;
  protected attendanceRecord: any;
  showMenuToggle = false;
  station?: iStationResult;

  get isStartup(): boolean {
    return (
      this._dataServ.empTypeValue === 's' ||
      !!this._dataServ.employeeValue?.isStartup
    );
  }

  get isWorkspaceAdmin(): boolean {
    const emp = this._dataServ.employeeValue;
    const roleName = (
      emp?.empRole?.name ||
      emp?.empRole ||
      this.currentUser?.empRole?.name ||
      this.currentUser?.empRole ||
      ''
    ).toLowerCase();
    return roleName === 'workspace-admin' || roleName === 'workspaceadmin';
  }

  protected statuses: status[] = [
    {
      id: 1,
      status: 'Break',
      hex: '#FFA500',
    },
    {
      id: 0,
      status: 'Available',
      hex: '#28c76f',
    },
  ];
  protected colors = {
    active: '#28c76f',
    signedOut: '#a8d0ff',
    remote: '#4d2d6e',
    absent: '#e93015',
    present: '#e78127',
    idle: '#acaeab',
    break: '#FFA500',
  };

  changeStatus(status: int) {
    this._employeeServ
      .ChangeStatus({
        employeeId: this._dataServ.employeeValue.id,
        status,
      })
      .subscribe({
        next: (res) => {
          this._dataServ.setEmployee({
            ...this._dataServ.employeeValue,
            status: res.status,
          });
        },
        error: (err) => {},
      });
  }

  @HostBinding('class.fixed-top')
  public isFixed = false;

  @HostBinding('class.navbar-static-style-on-scroll')
  public windowScrolled = false;

  // Add .navbar-static-style-on-scroll on scroll using HostListener & HostBinding
  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (
      (window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop > 100) &&
      this.coreConfig.layout.navbar.type == 'navbar-static-top' &&
      this.coreConfig.layout.type == 'horizontal'
    ) {
      this.windowScrolled = true;
    } else if (
      (this.windowScrolled && window.pageYOffset) ||
      document.documentElement.scrollTop ||
      document.body.scrollTop < 10
    ) {
      this.windowScrolled = false;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;

    if (!target?.closest('.language-switcher')) {
      this.languageDropdownOpen = false;
    }
  }

  // Private
  private _unsubscribeAll: Subject<any>;
  private readonly _languageStorageKey = 'osquare.setup.language';
  protected profilePicUrl!: string;
  constructor(
    private _router: Router,
    private _coreConfigService: CoreConfigService,
    private _coreMediaService: CoreMediaService,
    private _coreSidebarService: CoreSidebarService,
    private _mediaObserver: MediaObserver,
    private _dataServ: DataService,
    private _authServ: AuthService,
    private _employeeServ: EmployeeService,
    private _attendanceServ: AttendanceService,
    private _coreMenuService: CoreMenuService,
    private _modalServ: NgbModal,
    private _releaseNotesUi: ReleaseNotesUiService,
    private _releaseNotesGate: ReleaseNotesGateService,
    private _translateService: TranslateService,
    private _portalServ: CustomerPortalService,
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();
    this._initializeLanguage();
    this._syncSetupRoute(this._router.url);
    this._updateCustomerPortalState(this._router.url);

    this._router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntil(this._unsubscribeAll),
      )
      .subscribe((event) => {
        const currentUrl = event.urlAfterRedirects || event.url;
        this._syncSetupRoute(currentUrl);
        this._updateCustomerPortalState(currentUrl);
      });

    this._translateService.onLangChange
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ lang }) => {
        this.currentLanguage = lang === 'ar' ? 'ar' : 'en';
      });

    _dataServ.user$.pipe(takeUntil(this._unsubscribeAll)).subscribe({
      next: (user) => {
        this.currentUser = user ? { ...user } : user;
      },
    });

    this.role = this._dataServ.roleValue;
    if (this._dataServ.roleValue == 'employee') {
      _dataServ.employee$.pipe(takeUntil(this._unsubscribeAll)).subscribe({
        next: (employee) => {
          if (employee) {
            this.currentUser.profilePic = employee.profilePic;
            this.currentUser.name = employee.name;
            this.currentUser.firstName = employee.firstName;
            this.currentUser.status = employee.status;
            this.currentUser.empRole = employee.empRole?.name;
            this.currentUser.employeeId = employee.id;
            this.currentUser.designation = employee.designation;
            this.currentUser.empRole = employee.empRole;
          }
        },
      });
      this._attendanceServ
        .GetManagementDashboardRecord(this.currentUser.employeeId)
        .pipe(map((res) => res[0]))
        .subscribe({
          next: (res) => {
            this.attendanceRecord = res;
            _employeeServ.userStatusValue = res;
            this.currentUser.profilePic = res.profilePic;
            this.currentUser.name = res.employee[0];
            if (res.signIn) {
              if (res.type == 'On Site' || !res.type)
                this.attendanceColor = this.colors.present;
              if (res.type == 'Remote')
                this.attendanceColor = this.colors.remote;

              if (res.status == 1) this.statusColor = this.colors.break;
              else if (res.projectName == 'Available')
                this.statusColor = this.colors.idle;
              else this.statusColor = this.colors.active;
            } else {
              this.attendanceColor = this.colors.absent;
              this.statusColor = null;
            }
            if (res.signOut) {
              this.attendanceColor = this.colors.signedOut;
              this.statusColor = null;
            }
          },
          error: (err) => {
            console.warn(err, 'Something went wrong..');
          },
        });
    } else {
      const customer = this._portalServ.getCustomerInfo();
      if (customer) {
        this.currentUser = {
          name: customer.customerName,
          firstName: customer.customerName.split(' ')[0],
          designation: 'Customer',
          empRole: 'Customer',
        };
        return;
      }
      _dataServ.customer$.pipe(takeUntil(this._unsubscribeAll)).subscribe({
        next: (customer) => {
          if (customer && this.currentUser) {
            this.currentUser = {
              ...this.currentUser,
              profilePic:
                customer.companyLogo ||
                '../../../../assets/images/avatars/0.png',
              name: customer.contactPersonName,
            };
          }
        },
      });
    }
    if (_dataServ.tokenValue && _dataServ.roleValue == 'employee') {
      if (_employeeServ.connection.state == 'Disconnected') {
        _employeeServ.start();
      }
      this._employeeServ.userStatus$.subscribe({
        next: (val: any) => {
          if (val.employeeId == this.currentUser?.employeeId) {
            if (val.signIn && !this.attendanceRecord.signIn) {
              this.attendanceRecord.signIn = val.signIn;
              this.attendanceRecord.type = val.type;
              if (val.type == 'On Site')
                this.attendanceColor = this.colors.present;
              if (val.type == 'Remote')
                this.attendanceColor = this.colors.remote;
            }
            if (val.signOut && !this.attendanceRecord.signOut) {
              this.attendanceRecord.signOut = val.signOut;
              this.attendanceColor = this.colors.signedOut;
              this.statusColor = null;
              return;
            }
            if (!val.signOut && !this.attendanceRecord?.signOut) {
              // console.log(val, this.attendanceRecord);

              if (val.status == 1) this.statusColor = this.colors.break;
              if (val.status == 0)
                if (this.attendanceRecord?.projectName != 'Available')
                  this.statusColor = this.colors.active;
                else this.statusColor = this.colors.idle;

              if (val.projectName && val.projectName == 'Available')
                this.statusColor = this.colors.idle;
              else if (val.projectName && val.projectName != 'Available')
                this.statusColor = this.colors.active;
            }
          }
        },
      });
    }
  }

  // Public Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   *
   * @param key
   */
  toggleSidebar(key: string): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }

  toggleLanguageDropdown(event?: Event): void {
    event?.stopPropagation();
    this.languageDropdownOpen = !this.languageDropdownOpen;
  }

  setLanguage(language: 'en' | 'ar'): void {
    if (language === this.currentLanguage) {
      this.languageDropdownOpen = false;
      return;
    }

    this.currentLanguage = language;
    this.languageDropdownOpen = false;
    localStorage.setItem(this._languageStorageKey, language);
    this._translateService.use(language);
  }

  /**
   * Toggle Dark Skin
   */
  toggleDarkSkin() {
    // Get the current skin
    this._coreConfigService
      .getConfig()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.currentSkin = config.layout.skin;
      });

    // Toggle Dark skin with prevSkin skin
    var skin = localStorage.getItem('prevSkin');
    if (skin) this.prevSkin = skin;

    if (this.currentSkin === 'dark') {
      this._coreConfigService.setConfig(
        { layout: { skin: this.prevSkin ? this.prevSkin : 'default' } },
        { emitEvent: true },
      );
    } else {
      localStorage.setItem('prevSkin', this.currentSkin);
      this._coreConfigService.setConfig(
        { layout: { skin: 'dark' } },
        { emitEvent: true },
      );
    }
  }

  /**
   * Logout method
   */
  logout() {
    if (this.isCustomerPortalUser) {
      this._portalServ.logout();
      return;
    }
    this._authServ.Logout();
  }

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // get the currentUser details from localStorage
    this._coreConfigService.setConfig(
      { layout: { animation: 'none' } },
      { emitEvent: true },
    );
    this._coreMenuService.onMenuChanged.subscribe((v) => {
      this.showMenuToggle = Boolean(v);
    });
    // Subscribe to the config changes
    this._coreConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.coreConfig = config;
        this.horizontalMenu = config.layout.type === 'horizontal';
        this.hiddenMenu = config.layout.menu.hidden === true;
        this.currentSkin = config.layout.skin;

        // Fix: for vertical layout if default navbar fixed-top than set isFixed = true
        if (this.coreConfig.layout.type === 'vertical') {
          setTimeout(() => {
            if (this.coreConfig.layout.navbar.type === 'fixed-top') {
              this.isFixed = true;
            }
          }, 0);
        }
      });

    // Horizontal Layout Only: Add class fixed-top to navbar below large screen
    if (this.coreConfig.layout.type == 'horizontal') {
      // On every media(screen) change
      this._coreMediaService.onMediaUpdate
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(() => {
          const isFixedTop = this._mediaObserver.isActive('bs-gt-xl');
          if (isFixedTop) {
            this.isFixed = false;
          } else {
            this.isFixed = true;
          }
        });
    }
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
  hexToRgbA(hex: string) {
    var c: any;
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
      c = hex.substring(1).split('');
      if (c.length == 3) {
        c = [c[0], c[0], c[1], c[1], c[2], c[2]];
      }
      c = '0x' + c.join('');
      return (
        'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',1)'
      );
    }
    throw new Error('Bad Hex');
  }
  setMenu(menu: string) {
    this._coreMenuService.setCurrentMenu(menu);
    sessionStorage.setItem('lastMenu', menu);
  }

  handleStationChange(station: iStationResult) {
    this.station = station;
  }

  openAttendanceModal() {
    if (this._dataServ.hasPermission('F1005'))
      this._modalServ.open(this.attendanceAdd.addAttendanceModal, {
        scrollable: false,
        centered: true,
        beforeDismiss: (): boolean => {
          setTimeout(() => {
            this.attendanceAdd.close();
          }, 500);
          return true;
        },
        keyboard: false,
        size: 'md',
      });
  }

  openReleaseNotes() {
    this._releaseNotesUi.open();
  }

  private _initializeLanguage(): void {
    const storedLanguage = localStorage.getItem(this._languageStorageKey);

    this.currentLanguage = storedLanguage === 'ar' ? 'ar' : 'en';

    if (
      (this._translateService.currentLang ||
        this._translateService.defaultLang) !== this.currentLanguage
    ) {
      this._translateService.use(this.currentLanguage);
    }
  }

  private _syncSetupRoute(url: string): void {
    const normalizedUrl = (url || '').split('?')[0].split('#')[0];
    this.showSetupLanguageSwitcher =
      normalizedUrl === '/setup' || normalizedUrl.startsWith('/setup/');
  }

  private _updateCustomerPortalState(url?: string): void {
    const currentUrl = url || this._router.url || '';
    this.isCustomerPortalUser =
      this._portalServ.isLoggedIn() || currentUrl.includes('customer-portal');
  }
}
